import { UserService } from 'src/app/user/services/user.service';

import { IsNotEmptyObjectPipe } from '../../../../../shared/pipes/is-not-empty-object.pipe';
import { IsNilPipe } from 'src/app/pipes/is-nil.pipe';
import { CountUserProductStationsPipe } from 'src/app/user/pipes/count-user-product-stations.pipe';
import { CountUserProductsPipe } from 'src/app/user/pipes/count-user-products.pipe';
import { EppProductsInListPipe } from 'src/app/user/pipes/epp-products-in-list.pipe';
import { HasUserEopProductPipe } from 'src/app/user/pipes/has-user-eop-product.pipe';
import { HasUserEppProductPipe } from 'src/app/user/pipes/has-user-epp-product.pipe';
import { HasUserProductPipe } from 'src/app/user/pipes/has-user-product.pipe';
import { HasUserProductTypePipe } from 'src/app/user/pipes/has-user-product-type.pipe';
import { HasUserSmwProductPipe } from 'src/app/user/pipes/has-user-smw-product.pipe';
import { HasUserStationPipe } from 'src/app/user/pipes/has-user-station.pipe';

import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatStepperModule } from '@angular/material/stepper';
import { RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { ClickSectionCheckbox } from '../../models/click-section-checkbox.interface';
import { MatErrorMessagesDirective } from '@wettermanufaktur-gmbh/ui-sdk/directives/mat-error-messages';
import { map, merge, startWith } from 'rxjs';
import { UserStationChange } from 'src/app/user/models/user-station-change.interface';

export const MODULES = [
  CommonModule,
  ReactiveFormsModule,
  MatCardModule,
  MatCheckboxModule,
  MatFormFieldModule,
  MatInputModule,
  MatDatepickerModule,
  RouterModule,
  MatListModule,
  MatExpansionModule,
  MatButtonModule,
  MatStepperModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [
  IsNotEmptyObjectPipe,
  IsNilPipe,
  HasUserEppProductPipe,
  HasUserEopProductPipe,
  HasUserSmwProductPipe,
  HasUserProductPipe,
  CountUserProductStationsPipe,
  EppProductsInListPipe,
  CountUserProductsPipe,
  HasUserStationPipe,
  HasUserProductTypePipe,
];
export const DIRECTIVES = [MatErrorMessagesDirective];

@Component({
  selector: 'app-users-tab-app',
  templateUrl: './users-tab-app.component.html',
  styleUrls: ['../../../../user-admin.component.scss'],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class UsersTabAppComponent implements OnInit {
  userService = inject(UserService);
  injector = inject(Injector);

  newUser = input<boolean>();
  isSaving = input<boolean>();
  contingents = input<any>();
  buttonsTemplate = input<TemplateRef<any>>();
  productList = input<any>();
  eopTypes = input<string[]>();
  egpTypes = input<any>();

  clickSectionCheckbox = output<ClickSectionCheckbox>();
  clickAllProducts = output<ClickSectionCheckbox>();
  expandedChanged = output<string>();
  userStationChange = output<UserStationChange>();
  userProductChange = output<UserStationChange>();
  cancel = output();
  summarizeUser = output();

  accordionEppApp = viewChild<MatAccordion>('accordionEppApp');
  accordionEopApp = viewChild<MatAccordion>('accordionEopApp');
  accordionEgpApp = viewChild<MatAccordion>('accordionEgpApp');
  accordionEgwApp = viewChild<MatAccordion>('accordionEgwApp');
  accordionSmwApp = viewChild<MatAccordion>('accordionSmwApp');
  accordionRadarApp = viewChild<MatAccordion>('accordionRadarApp');

  dateToFormControlErrors = signal<ValidationErrors | null>(null);
  activeFormControlValue = signal<any>(null);

  ngOnInit(): void {
    this.setDateToFormControlErrors();
    this.setActiveFormControlValue();
  }

  onClickSectionCheckbox(event: ClickSectionCheckbox) {
    this.clickSectionCheckbox.emit(event);
  }

  onClickAllProducts(event: ClickSectionCheckbox) {
    this.clickAllProducts.emit(event);
  }

  onExpandedChanged(section: string) {
    this.expandedChanged.emit(section);
  }

  onUserStationChange(event: UserStationChange) {
    this.userStationChange.emit(event);
  }

  onUserProductChange(event: UserStationChange) {
    this.userProductChange.emit(event);
  }

  onCancel() {
    this.cancel.emit();
  }

  onSummarizeUser() {
    this.summarizeUser.emit();
  }

  private setDateToFormControlErrors() {
    effect(
      onCleanup => {
        const fg = this.userService.formGroupApp;
        const control = fg?.get('dateTo');
        if (!control) {
          this.dateToFormControlErrors.set(null);
          return;
        }

        const sub = merge(control.valueChanges, control.statusChanges)
          .pipe(
            startWith(null),
            map(() => control.errors)
          )
          .subscribe(errs => this.dateToFormControlErrors.set(errs));

        onCleanup(() => {
          sub.unsubscribe();
          this.dateToFormControlErrors.set(null);
        });
      },
      { injector: this.injector }
    );
  }

  private setActiveFormControlValue() {
    effect(
      onCleanup => {
        const fg = this.userService.formGroupApp;
        const control = fg?.get('active');
        if (!control) {
          this.activeFormControlValue.set(null);
          return;
        }

        const sub = merge(control.valueChanges)
          .pipe(startWith(control.value))
          .subscribe(value => this.activeFormControlValue.set(value));

        onCleanup(() => {
          sub.unsubscribe();
          this.activeFormControlValue.set(null);
        });
      },
      { injector: this.injector }
    );
  }
}
